/**
 * Tool Combo Configuration Panel
 *
 * Split-view layout for managing tool combo templates:
 * - Left: List of templates
 * - Right: Template editor form
 *
 * Tool combos define which tools to use, in what order, and with what instructions.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Loading } from '@/components/common';
import {
  Save,
  RotateCcw,
  Search,
  ChevronRight,
  Check,
  Plus,
  Trash2,
  Eye,
  Layers,
  X,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';
import { useToolComboConfig, useUpdateToolComboConfig, useToolEnhancementConfig } from '../hooks/useSystemConfig';
import { useToolsQuery } from '@/lib/queries';
import { useI18n } from '@/i18n';
import { systemApi } from '@/lib/api/system';
import type { ToolComboConfig, ToolComboTemplate, ToolComboTool } from '@/types/system';

export const ToolComboPanel: React.FC = () => {
  const { messages, language } = useI18n();
  const t = messages.systemSettings?.toolCombo;
  const isChinese = language === 'zh';

  // Fetch available tools
  const { data: toolsData, isLoading: toolsLoading } = useToolsQuery();

  // Fetch tool enhancement config for display names
  const { data: enhancementConfig } = useToolEnhancementConfig();

  // Fetch current config
  const { data: config, isLoading: configLoading, refetch } = useToolComboConfig();
  const updateConfig = useUpdateToolComboConfig();

  // Local state
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [localConfig, setLocalConfig] = useState<Record<string, ToolComboTemplate>>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newTemplateId, setNewTemplateId] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [previewContent, setPreviewContent] = useState('');
  const [toolSearchQuery, setToolSearchQuery] = useState('');
  const [showToolPicker, setShowToolPicker] = useState(false);

  // Preview mutation
  const previewMutation = useMutation({
    mutationFn: async (templateId: string) => {
      const result = await systemApi.toolCombo.preview(templateId, isChinese ? 'cn' : 'en');
      return result.context;
    },
    onSuccess: (content) => {
      setPreviewContent(content);
      setShowPreview(true);
    },
  });

  // Sync config from server
  useEffect(() => {
    if (config?.templates) {
      setLocalConfig(config.templates);
      setHasChanges(false);
    }
  }, [config]);

  // Reset tool picker when switching templates
  useEffect(() => {
    setToolSearchQuery('');
    setShowToolPicker(false);
  }, [selectedTemplateId]);

  // Get all available tool names from toolsData with enhancement info
  const availableTools = useMemo(() => {
    if (!toolsData) return [];
    const enhancementTools = enhancementConfig?.tools || {};

    const tools: {
      name: string;
      description: string;
      category: string;
      type: 'builtin' | 'custom';
      // Enhancement fields for search and display
      displayNameCn?: string;
      displayNameEn?: string;
      descriptionCn?: string;
      descriptionEn?: string;
    }[] = [];

    // Add builtin toolkits
    toolsData.builtin_toolkits?.forEach((toolkit) => {
      const toolName = toolkit.name.replace('builtin:', '');
      const enhancement = enhancementTools[toolName];
      tools.push({
        name: toolName,
        description: toolkit.description,
        category: toolkit.category || 'other',
        type: 'builtin',
        displayNameCn: enhancement?.display_name_cn,
        displayNameEn: enhancement?.display_name_en,
        descriptionCn: enhancement?.description_cn,
        descriptionEn: enhancement?.description_en,
      });
    });

    // Add custom tools
    toolsData.custom_tools?.forEach((tool) => {
      const enhancement = enhancementTools[tool.name];
      tools.push({
        name: tool.name,
        description: tool.description,
        category: tool.category || 'custom',
        type: 'custom',
        displayNameCn: enhancement?.display_name_cn,
        displayNameEn: enhancement?.display_name_en,
        descriptionCn: enhancement?.description_cn,
        descriptionEn: enhancement?.description_en,
      });
    });

    return tools.sort((a, b) => a.name.localeCompare(b.name));
  }, [toolsData, enhancementConfig]);

  // Filter available tools by search (all language variants)
  const filteredAvailableTools = useMemo(() => {
    if (!toolSearchQuery) return availableTools;
    const query = toolSearchQuery.toLowerCase();
    return availableTools.filter((tool) => {
      // Search in all fields: name, original description, category, and all enhancement fields
      return (
        tool.name.toLowerCase().includes(query) ||
        tool.description.toLowerCase().includes(query) ||
        tool.category.toLowerCase().includes(query) ||
        tool.displayNameCn?.toLowerCase().includes(query) ||
        tool.displayNameEn?.toLowerCase().includes(query) ||
        tool.descriptionCn?.toLowerCase().includes(query) ||
        tool.descriptionEn?.toLowerCase().includes(query)
      );
    });
  }, [availableTools, toolSearchQuery]);

  // Get tools already added to current template
  const addedToolNames = useMemo(() => {
    if (!selectedTemplateId || !localConfig[selectedTemplateId]) return new Set<string>();
    return new Set(localConfig[selectedTemplateId].tools.map((t) => t.tool_name));
  }, [selectedTemplateId, localConfig]);

  // Filter templates by search
  const filteredTemplates = useMemo(() => {
    const templates = Object.values(localConfig);
    if (!searchQuery) return templates;

    const query = searchQuery.toLowerCase();
    return templates.filter((t) => {
      const name = isChinese ? t.name_cn : t.name_en;
      return (
        t.id.toLowerCase().includes(query) ||
        name?.toLowerCase().includes(query) ||
        t.description_cn?.toLowerCase().includes(query) ||
        t.description_en?.toLowerCase().includes(query)
      );
    });
  }, [localConfig, searchQuery, isChinese]);

  // Get selected template
  const selectedTemplate = selectedTemplateId ? localConfig[selectedTemplateId] : null;

  // Helper to get display name
  const getDisplayName = (template: ToolComboTemplate): string => {
    if (isChinese) {
      return template.name_cn || template.name_en || template.id;
    }
    return template.name_en || template.name_cn || template.id;
  };

  // Update template field
  const updateTemplate = (templateId: string, field: keyof ToolComboTemplate, value: unknown) => {
    setLocalConfig((prev) => ({
      ...prev,
      [templateId]: {
        ...prev[templateId],
        [field]: value,
      },
    }));
    setHasChanges(true);
  };

  // Update tool in template
  const updateTool = (templateId: string, toolIndex: number, field: keyof ToolComboTool, value: unknown) => {
    setLocalConfig((prev) => {
      const template = prev[templateId];
      if (!template) return prev;

      const newTools = [...template.tools];
      newTools[toolIndex] = { ...newTools[toolIndex], [field]: value };

      return {
        ...prev,
        [templateId]: {
          ...template,
          tools: newTools,
        },
      };
    });
    setHasChanges(true);
  };

  // Add tool to template (with optional tool info for auto-fill)
  const addTool = (templateId: string, toolInfo?: { name: string; description: string }) => {
    setLocalConfig((prev) => {
      const template = prev[templateId];
      if (!template) return prev;

      // If tool is already added, skip
      if (toolInfo && template.tools.some((t) => t.tool_name === toolInfo.name)) {
        return prev;
      }

      const maxOrder = Math.max(0, ...template.tools.map((t) => t.order));
      const newTool: ToolComboTool = {
        tool_name: toolInfo?.name || '',
        order: maxOrder + 1,
        // Auto-fill usage instruction with tool description
        usage_instruction: toolInfo?.description || '',
      };

      return {
        ...prev,
        [templateId]: {
          ...template,
          tools: [...template.tools, newTool],
        },
      };
    });
    setHasChanges(true);
  };

  // Remove tool from template
  const removeTool = (templateId: string, toolIndex: number) => {
    setLocalConfig((prev) => {
      const template = prev[templateId];
      if (!template) return prev;

      const newTools = template.tools.filter((_, i) => i !== toolIndex);
      // Re-order remaining tools
      newTools.forEach((tool, i) => {
        tool.order = i + 1;
      });

      return {
        ...prev,
        [templateId]: {
          ...template,
          tools: newTools,
        },
      };
    });
    setHasChanges(true);
  };

  // Move tool up/down
  const moveTool = (templateId: string, toolIndex: number, direction: 'up' | 'down') => {
    setLocalConfig((prev) => {
      const template = prev[templateId];
      if (!template) return prev;

      const newTools = [...template.tools];
      const targetIndex = direction === 'up' ? toolIndex - 1 : toolIndex + 1;

      if (targetIndex < 0 || targetIndex >= newTools.length) return prev;

      // Swap
      [newTools[toolIndex], newTools[targetIndex]] = [newTools[targetIndex], newTools[toolIndex]];

      // Re-order
      newTools.forEach((tool, i) => {
        tool.order = i + 1;
      });

      return {
        ...prev,
        [templateId]: {
          ...template,
          tools: newTools,
        },
      };
    });
    setHasChanges(true);
  };

  // Create new template
  const handleCreateTemplate = () => {
    if (!newTemplateId.trim()) return;

    const id = newTemplateId.trim();
    if (localConfig[id]) {
      alert(isChinese ? '模板 ID 已存在' : 'Template ID already exists');
      return;
    }

    const newTemplate: ToolComboTemplate = {
      id,
      name_cn: '',
      name_en: '',
      description_cn: '',
      description_en: '',
      tools: [],
      guidance: '',
      enabled: true,
    };

    setLocalConfig((prev) => ({
      ...prev,
      [id]: newTemplate,
    }));
    setSelectedTemplateId(id);
    setIsCreating(false);
    setNewTemplateId('');
    setHasChanges(true);
  };

  // Delete template
  const handleDeleteTemplate = (templateId: string) => {
    if (!confirm(t?.confirmDelete || 'Are you sure you want to delete this template?')) return;

    setLocalConfig((prev) => {
      const newConfig = { ...prev };
      delete newConfig[templateId];
      return newConfig;
    });

    if (selectedTemplateId === templateId) {
      setSelectedTemplateId(null);
    }
    setHasChanges(true);
  };

  // Save config
  const handleSave = async () => {
    try {
      const newConfig: ToolComboConfig = {
        templates: localConfig,
      };
      await updateConfig.mutateAsync(newConfig);
      setHasChanges(false);
      alert(t?.saveSuccess || 'Saved successfully');
    } catch (error) {
      alert(`${t?.saveFailed || 'Save failed'}: ${error}`);
    }
  };

  // Reload from server
  const handleReload = () => {
    refetch();
  };

  // Handle preview
  const handlePreview = async () => {
    if (!selectedTemplateId) return;

    // Need to save first if there are changes
    if (hasChanges) {
      alert(isChinese ? '请先保存更改再预览' : 'Please save changes before previewing');
      return;
    }

    previewMutation.mutate(selectedTemplateId);
  };

  if (toolsLoading || configLoading) {
    return <Loading message={t?.loading || 'Loading...'} />;
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Layers className="w-5 h-5" />
            {t?.title || 'Tool Combos'}
          </h2>
          <p className="text-sm text-gray-500">{t?.description || 'Configure tool combination templates'}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={handleReload} disabled={updateConfig.isPending}>
            <RotateCcw className="w-4 h-4 mr-1" />
            {t?.reload || 'Reload'}
          </Button>
          <Button size="sm" onClick={handleSave} disabled={!hasChanges || updateConfig.isPending}>
            <Save className="w-4 h-4 mr-1" />
            {updateConfig.isPending ? (t?.saving || 'Saving...') : (t?.save || 'Save')}
          </Button>
        </div>
      </div>

      {/* Main Split View */}
      <div className="flex gap-4 h-[calc(100vh-280px)] min-h-[500px]">
        {/* Left Panel - Template List */}
        <Card className="w-1/3 flex flex-col overflow-hidden">
          {/* Search and Add */}
          <div className="p-3 border-b space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder={t?.searchPlaceholder || 'Search templates...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-9"
              />
            </div>
            {isCreating ? (
              <div className="flex gap-2">
                <Input
                  placeholder={t?.templateIdPlaceholder || 'Template ID (e.g., astro_basic)'}
                  value={newTemplateId}
                  onChange={(e) => setNewTemplateId(e.target.value)}
                  className="h-8 text-sm"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleCreateTemplate();
                    if (e.key === 'Escape') {
                      setIsCreating(false);
                      setNewTemplateId('');
                    }
                  }}
                />
                <Button size="sm" onClick={handleCreateTemplate} className="h-8">
                  <Check className="w-4 h-4" />
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setIsCreating(false);
                    setNewTemplateId('');
                  }}
                  className="h-8"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <Button variant="secondary" size="sm" className="w-full" onClick={() => setIsCreating(true)}>
                <Plus className="w-4 h-4 mr-1" />
                {t?.newTemplate || 'New Template'}
              </Button>
            )}
          </div>

          {/* Template List */}
          <div className="flex-1 overflow-y-auto">
            {filteredTemplates.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">{t?.noTemplateSelected || 'Select a template'}</div>
            ) : (
              filteredTemplates.map((template) => (
                <div
                  key={template.id}
                  onClick={() => setSelectedTemplateId(template.id)}
                  className={`px-3 py-2 cursor-pointer border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                    selectedTemplateId === template.id ? 'bg-blue-50 border-l-2 border-l-blue-500' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <Layers className="w-4 h-4 text-blue-500 flex-shrink-0" />
                      <span className="text-sm font-medium text-gray-900 truncate">{getDisplayName(template)}</span>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {template.enabled !== false && <Check className="w-4 h-4 text-green-500" />}
                      <span className="text-xs text-gray-400">{template.tools.length}</span>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 truncate mt-0.5 ml-6 font-mono">{template.id}</p>
                </div>
              ))
            )}
          </div>

          {/* Stats */}
          <div className="p-2 border-t bg-gray-50 text-xs text-gray-500">
            {(t?.templateCount || '{count} template(s)').replace('{count}', String(Object.keys(localConfig).length))}
          </div>
        </Card>

        {/* Right Panel - Template Form */}
        <Card className="flex-1 overflow-y-auto">
          {selectedTemplate ? (
            <div className="p-4 space-y-4">
              {/* Template ID (readonly) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t?.templateId || 'Template ID'}</label>
                <Input value={selectedTemplate.id} disabled className="font-mono bg-gray-50" />
              </div>

              {/* Names */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t?.nameCn || 'Name (Chinese)'}</label>
                  <Input
                    value={selectedTemplate.name_cn || ''}
                    onChange={(e) => updateTemplate(selectedTemplateId!, 'name_cn', e.target.value)}
                    placeholder="例如：基础占星组合"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t?.nameEn || 'Name (English)'}</label>
                  <Input
                    value={selectedTemplate.name_en || ''}
                    onChange={(e) => updateTemplate(selectedTemplateId!, 'name_en', e.target.value)}
                    placeholder="e.g., Basic Astrology Combo"
                  />
                </div>
              </div>

              {/* Descriptions */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t?.descriptionCn || 'Description (Chinese)'}</label>
                  <textarea
                    value={selectedTemplate.description_cn || ''}
                    onChange={(e) => updateTemplate(selectedTemplateId!, 'description_cn', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm resize-none"
                    rows={2}
                    placeholder="模板描述..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t?.descriptionEn || 'Description (English)'}</label>
                  <textarea
                    value={selectedTemplate.description_en || ''}
                    onChange={(e) => updateTemplate(selectedTemplateId!, 'description_en', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm resize-none"
                    rows={2}
                    placeholder="Template description..."
                  />
                </div>
              </div>

              {/* Enabled Toggle */}
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">{t?.enabled || 'Enabled'}</label>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedTemplate.enabled !== false}
                    onChange={(e) => updateTemplate(selectedTemplateId!, 'enabled', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              {/* Tools Section - Split View */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-gray-900">{t?.tools || 'Tools'}</h3>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowToolPicker(!showToolPicker)}
                  >
                    {showToolPicker ? <X className="w-4 h-4 mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
                    {showToolPicker ? (t?.closePicker || 'Close') : (t?.addTool || 'Add Tool')}
                  </Button>
                </div>

                {/* Tool Picker Panel */}
                {showToolPicker && (
                  <div className="mb-4 border rounded-lg bg-blue-50/50 overflow-hidden">
                    <div className="p-2 border-b bg-blue-50">
                      <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          placeholder={t?.searchTools || 'Search available tools...'}
                          value={toolSearchQuery}
                          onChange={(e) => setToolSearchQuery(e.target.value)}
                          className="pl-8 h-8 text-sm bg-white"
                        />
                      </div>
                    </div>
                    <div className="max-h-48 overflow-y-auto p-1">
                      {filteredAvailableTools.length === 0 ? (
                        <div className="text-center text-gray-400 py-4 text-sm">
                          {isChinese ? '没有找到匹配的工具' : 'No matching tools found'}
                        </div>
                      ) : (
                        <div className="space-y-0.5">
                          {filteredAvailableTools.map((tool) => {
                            const isAdded = addedToolNames.has(tool.name);
                            // Get display name based on language, fallback to tool name
                            const displayName = isChinese
                              ? (tool.displayNameCn || tool.displayNameEn || tool.name)
                              : (tool.displayNameEn || tool.displayNameCn || tool.name);
                            // Get description based on language, fallback to original
                            const displayDesc = isChinese
                              ? (tool.descriptionCn || tool.descriptionEn || tool.description)
                              : (tool.descriptionEn || tool.descriptionCn || tool.description);
                            return (
                              <button
                                key={tool.name}
                                onClick={() => {
                                  if (!isAdded) {
                                    // Use enhanced description if available
                                    addTool(selectedTemplateId!, { name: tool.name, description: displayDesc });
                                  }
                                }}
                                disabled={isAdded}
                                className={`w-full text-left px-2 py-1.5 rounded text-sm transition-colors ${
                                  isAdded
                                    ? 'bg-green-100 text-green-700 cursor-not-allowed'
                                    : 'hover:bg-blue-100 text-gray-700'
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2 min-w-0 flex-1">
                                    <span className="font-medium truncate">{displayName}</span>
                                    {displayName !== tool.name && (
                                      <span className="text-xs text-gray-400 font-mono">{tool.name}</span>
                                    )}
                                    <span className="text-xs text-gray-400 px-1 py-0.5 bg-gray-100 rounded">
                                      {tool.category}
                                    </span>
                                  </div>
                                  {isAdded ? (
                                    <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                                  ) : (
                                    <Plus className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                  )}
                                </div>
                                <p className="text-xs text-gray-500 truncate mt-0.5">{displayDesc}</p>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <div className="px-2 py-1.5 border-t bg-blue-50 text-xs text-gray-500">
                      {filteredAvailableTools.length} {isChinese ? '个工具可选' : 'tools available'} · {addedToolNames.size} {isChinese ? '个已添加' : 'added'}
                    </div>
                  </div>
                )}

                {/* Selected Tools List */}
                {selectedTemplate.tools.length === 0 ? (
                  <div className="text-center text-gray-400 py-4 border border-dashed rounded-lg">
                    {t?.noTools || 'No tools added'}
                    {!showToolPicker && (
                      <button
                        onClick={() => setShowToolPicker(true)}
                        className="block mx-auto mt-2 text-blue-500 text-sm hover:underline"
                      >
                        {t?.clickToAdd || 'Click to add tools'}
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {selectedTemplate.tools.map((tool, index) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-3 border">
                        <div className="flex items-start gap-2">
                          {/* Order controls */}
                          <div className="flex flex-col gap-0.5">
                            <button
                              onClick={() => moveTool(selectedTemplateId!, index, 'up')}
                              disabled={index === 0}
                              className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                            >
                              <ChevronUp className="w-4 h-4" />
                            </button>
                            <span className="text-xs text-gray-500 text-center">{tool.order}</span>
                            <button
                              onClick={() => moveTool(selectedTemplateId!, index, 'down')}
                              disabled={index === selectedTemplate.tools.length - 1}
                              className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                            >
                              <ChevronDown className="w-4 h-4" />
                            </button>
                          </div>

                          {/* Tool content */}
                          <div className="flex-1 space-y-2">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm text-gray-900">{tool.tool_name || (t?.selectTool || 'Select tool')}</span>
                              {!tool.tool_name && (
                                <select
                                  value={tool.tool_name}
                                  onChange={(e) => {
                                    const selectedTool = availableTools.find((t) => t.name === e.target.value);
                                    updateTool(selectedTemplateId!, index, 'tool_name', e.target.value);
                                    if (selectedTool && !tool.usage_instruction) {
                                      updateTool(selectedTemplateId!, index, 'usage_instruction', selectedTool.description);
                                    }
                                  }}
                                  className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded-md"
                                >
                                  <option value="">{t?.selectTool || 'Select tool'}</option>
                                  {availableTools.map((t) => (
                                    <option key={t.name} value={t.name}>
                                      {t.name}
                                    </option>
                                  ))}
                                </select>
                              )}
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">{t?.usageInstruction || 'Usage Instruction'}</label>
                              <textarea
                                value={tool.usage_instruction || ''}
                                onChange={(e) => updateTool(selectedTemplateId!, index, 'usage_instruction', e.target.value)}
                                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md resize-y min-h-[60px]"
                                rows={3}
                                placeholder={t?.usageInstructionPlaceholder || 'Describe when and how to use this tool...'}
                              />
                            </div>
                          </div>

                          {/* Remove button */}
                          <button
                            onClick={() => removeTool(selectedTemplateId!, index)}
                            className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Guidance */}
              <div className="border-t pt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t?.guidance || 'Guidance'}</label>
                <textarea
                  value={selectedTemplate.guidance || ''}
                  onChange={(e) => updateTemplate(selectedTemplateId!, 'guidance', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm resize-y min-h-[80px]"
                  rows={4}
                  placeholder={t?.guidancePlaceholder || 'Additional guidance for using this tool combo...'}
                />
              </div>

              {/* Actions */}
              <div className="border-t pt-4 flex items-center justify-between">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handlePreview}
                  disabled={previewMutation.isPending || hasChanges}
                  title={hasChanges ? (isChinese ? '请先保存' : 'Save first') : ''}
                >
                  <Eye className="w-4 h-4 mr-1" />
                  {previewMutation.isPending ? '...' : (t?.preview || 'Preview')}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleDeleteTemplate(selectedTemplateId!)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  {t?.deleteTemplate || 'Delete'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              <div className="text-center">
                <Layers className="w-12 h-12 mx-auto mb-2 opacity-20" />
                <p>{t?.noTemplateSelected || 'Select a template'}</p>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Eye className="w-5 h-5 text-blue-600" />
                {t?.previewPrompt || 'Preview Prompt'}
              </h3>
              <button onClick={() => setShowPreview(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <pre className="text-sm text-gray-800 bg-gray-100 rounded-lg p-4 whitespace-pre-wrap font-mono">
                {previewContent}
              </pre>
            </div>
            <div className="flex items-center justify-end px-4 py-3 border-t bg-gray-50">
              <Button variant="secondary" size="sm" onClick={() => setShowPreview(false)}>
                {t?.closePreview || 'Close'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
